import { useQuery } from "@tanstack/react-query";
import { StatGrid } from "@/components/StatGrid";
import { PageHead } from "@/components/PageHead";
import { POLL } from "@/lib/format";

import { fota } from "@/api/client";
import { Donut } from "@/components/Donut";

const VER_COLORS = ["var(--teal)", "var(--info)", "var(--warn)", "var(--t-dim)", "var(--line2)"];

/** Live fleet firmware-version distribution (OFT-07), sourced from
 *  device_fota_state — the version each device last reported over health. */
export function FotaOverviewPage() {
  const dist = useQuery({ queryKey: ["fota", "dist"], queryFn: fota.fwDistribution, refetchInterval: POLL.DASHBOARD });
  const slices = dist.data?.slices ?? [];
  const total = slices.reduce((a, s) => a + s.count, 0);

  const bare = slices.filter((s) => !s.app_ver).reduce((a, s) => a + s.count, 0);
  const versions = slices.filter((s) => s.app_ver);
  const latest = versions.length ? Math.max(...versions.map((s) => s.app_ver ?? 0)) : null;
  const onLatest = versions.filter((s) => s.app_ver === latest).reduce((a, s) => a + s.count, 0);
  const behind = total - bare - onLatest;

  return (
    <div className="mpage on">
      <PageHead eyebrow="FOTA · Overview" title="Firmware across the fleet" />

      <div className="section-label">Fleet</div>
      <StatGrid stats={[
        { k: "Devices reporting", v: total, s: "seen via health check-ins" },
        { k: `On latest ${latest ? `· v${latest}` : ""}`, v: onLatest, s: `${total ? Math.round((onLatest / total) * 100) : 0}% of fleet` },
        { k: "Behind", v: behind, s: "candidates for a rollout" },
        { k: "Bare / no app", v: bare, s: "awaiting first install" },
      ]} />

      <div className="section-label">Software version distribution — telematics devices</div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">Devices by firmware version</div>
          <Donut
            slices={slices.map((s, i) => ({
              label: s.app_ver ? `v${s.app_ver}` : "none / bare",
              value: s.count,
              color: s.app_ver ? VER_COLORS[i % VER_COLORS.length] : "var(--warn)",
              display: `${s.count.toLocaleString("en-IN")} dev`,
            }))}
          />
        </div>
      </div>
    </div>
  );
}
